import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import GenericSelectPicker from "../../../../components/SelectPicker/GenericSelectPicker";
import { IProject } from "../../../../models/project.model";
import { GeneralInformationProject } from "../../../../models/workflow.model";
import projectsService from "../../../../services/projects.service";
import styles from "./GeneralInformation.module.scss";
import sharedStyles from "../../../../assets/shared.module.scss";

import { isValidHttpUrl } from "../../../../misc/utils.functions";
import { RemoveIcon } from "../../../../assets/icons";

interface Props {
  visible: boolean;
  isReadOnly: boolean;
  currentProjects: GeneralInformationProject[];
  onAdd: (project: GeneralInformationProject) => void;
  onRemove: (index: number) => void;
  onBlur: (event: any) => void;
}

const ProjectSection: React.FC<Props> = ({ visible, isReadOnly, currentProjects, onAdd, onRemove, onBlur }) => {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const [hasCBA, setHasCBA] = useState<string>("false");
  const [cbaURL, setCbaURL] = useState<string>("");
  const [invalidUrlError, setInvalidUrlError] = useState<string>();
  const MAX_PROJECTS_ALLOWED = 5;

  const assignProject = () => {
    if (Boolean(selectedProject)) {
      const project: GeneralInformationProject = {
        generalInformationId: null,
        cba: cbaURL,
        externalProject: selectedProject!,
      };

      onAdd(project);
      setSelectedProject(null);
      setHasCBA("false");
      setCbaURL("");
    }
  };

  const onSearch = (query: string) =>
    projectsService.search(
      query,
      currentProjects.map((x) => x.externalProject.transactionNumber)
    );

  return visible ? (
    <Box className={styles.projectSection}>
      <Box className={styles.newProjectContainer}>
        <div>
          <FormControl fullWidth>
            <FormLabel id="selectedProject-label">{t("projectTransactionNumber")}</FormLabel>
            <GenericSelectPicker
              id="selectedProject"
              defaultValue={selectedProject}
              placeholder={t("projectTransactionNumber")!}
              disabled={currentProjects.length >= MAX_PROJECTS_ALLOWED}
              readOnly={isReadOnly}
              onChange={(newValue: IProject | null) => {
                setSelectedProject(newValue);
              }}
              onBlur={onBlur}
              search={onSearch}
              getOptionLabel={(opt: IProject) => `[${opt.transactionNumber}] - ${opt.projectName}`}
              isOptionEqualToValue={(opt: IProject, value: IProject) =>
                opt.transactionNumber === value.transactionNumber
              }
            />
          </FormControl>
        </div>
        <div className={styles.radioGroupCBA}>
          <FormControl fullWidth>
            <FormLabel id="hasCBA-label">{t("hasCBA")}</FormLabel>
            <RadioGroup
              aria-labelledby="hasCBA-group-label"
              name="hasCBA"
              row
              value={hasCBA}
              onChange={(e) => {
                setHasCBA(e.currentTarget.value);
                if (e.currentTarget.value !== "true") {
                  setCbaURL("");
                }
              }}
              onBlur={onBlur}
              className={!Boolean(selectedProject) ? sharedStyles.disabledControl : ""}
            >
              <FormControlLabel
                value={"true"}
                control={<Radio inputProps={{ disabled: !Boolean(selectedProject) || isReadOnly }} />}
                label={t("yes")}
              />
              <FormControlLabel
                value={"false"}
                control={<Radio inputProps={{ disabled: !Boolean(selectedProject) || isReadOnly }} />}
                label={t("no")}
              />
            </RadioGroup>
          </FormControl>
        </div>
        <div>
          <FormControl fullWidth>
            <FormLabel id="cba-label">{t("cbaLabel")}</FormLabel>
            <TextField
              id="cba"
              name="cba"
              placeholder={t("cbaURL")!}
              fullWidth
              inputProps={{ readOnly: isReadOnly, maxLength: 2048 }}
              disabled={!Boolean(selectedProject) || hasCBA === "false"}
              value={cbaURL}
              onChange={(event) => {
                setCbaURL(event.currentTarget.value);
              }}
              onBlur={(e) => {
                onBlur(e);
                const isValid = e.currentTarget.value ? isValidHttpUrl(e.currentTarget.value) : true;
                if (isValid) setInvalidUrlError(undefined);
                else setInvalidUrlError(t("invalidUrl")!);
              }}
              helperText={invalidUrlError}
              error={Boolean(invalidUrlError)}
            />
          </FormControl>
        </div>
        <div className={styles.assign}>
          <Button
            variant="text"
            onClick={assignProject}
            disabled={!Boolean(selectedProject) || Boolean(invalidUrlError)}
          >
            {t("assign")}
          </Button>
        </div>
        <div className={styles.info}>
          {currentProjects.length === 0 && <label className={styles.error}>{t("projectsRequired")}</label>}
          <label className={styles.warningInfo}>{t("maxProjectAllowedWarning")}</label>
        </div>
      </Box>
      {currentProjects.length > 0 && (
        <TableContainer className={styles.assignedProjectsContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={styles.transactionNumber}>{t("transactionNumber")}</TableCell>
                <TableCell className={styles.projectName}>{t("projectName")}</TableCell>
                <TableCell className={styles.cba}>{t("cba")}</TableCell>
                <TableCell className={styles.actions}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentProjects.map((x, index: number) => (
                <TableRow key={`assigned_project_${x.externalProject.id}`}>
                  <TableCell className={styles.transactionNumber}>{x.externalProject.transactionNumber}</TableCell>
                  <TableCell className={styles.projectName}>{x.externalProject.projectName}</TableCell>
                  <TableCell className={styles.cba}>{x.cba}</TableCell>
                  <TableCell>
                    <Tooltip title={t("delete")} disableInteractive arrow>
                      <IconButton
                        disabled={isReadOnly}
                        onClick={() => {
                          onRemove(index);
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  ) : (
    <></>
  );
};

export default React.memo(ProjectSection);
